import { supabase } from './supabase.js';
import { TRANSACTION_LOGISTICS_NESTED } from '../marketplace/constants/transactionLogistics.js';

const PICKUP_PACKAGE_SELECT = `
  id,
  partner_id,
  listing_id,
  transaction_id,
  buyer_profile_id,
  status,
  codigo_recepcion,
  codigo_entrega,
  partner_notes,
  partner_outbound_tracking,
  partner_outbound_shipped_at,
  created_at,
  updated_at,
  transaction:transactions (
    ${TRANSACTION_LOGISTICS_NESTED}
  ),
  listing:listings (
    id,
    title,
    artist,
    seller_id,
    ships_pickup,
    status,
    cover_url,
    photos,
    profiles ( username )
  )
`;

/**
 * @param {string} partnerId
 * @returns {Promise<{ data: object[], error: string|null }>}
 */
export async function fetchPartnerPickupPackages(partnerId) {
  if (!partnerId) return { data: [], error: 'missing_partner' };
  const { data, error } = await supabase
    .from('partner_pickup_packages')
    .select(PICKUP_PACKAGE_SELECT)
    .eq('partner_id', partnerId)
    .order('updated_at', { ascending: false })
    .limit(200);
  if (error) {
    console.error('[partnerPickupService] fetch:', error.message);
    return { data: [], error: error.message };
  }
  return { data: data ?? [], error: null };
}

/**
 * @param {string} listingId
 * @returns {Promise<{ row: object|null, error: string|null }>}
 */
export async function fetchListingRowForPickupPreview(listingId) {
  if (!listingId) return { row: null, error: 'missing_listing' };
  const { data, error } = await supabase
    .from('listings')
    .select('id, title, artist, seller_id, ships_pickup, status, cover_url, photos')
    .eq('id', listingId)
    .maybeSingle();
  if (error) {
    console.error('[partnerPickupService] listing preview:', error.message);
    return { row: null, error: error.message };
  }
  return { row: data ?? null, error: null };
}

/**
 * @param {{ partnerId: string, listingId: string, transactionId?: string|null, buyerProfileId?: string|null }} p
 */
export async function insertPartnerPickupPackage(p) {
  if (!p?.partnerId || !p?.listingId) return { error: 'Parámetros incompletos' };
  const row = {
    partner_id: p.partnerId,
    listing_id: p.listingId,
    status: 'asignado',
    transaction_id: p.transactionId?.trim() ? p.transactionId.trim() : null,
    buyer_profile_id: p.buyerProfileId?.trim() ? p.buyerProfileId.trim() : null,
  };
  const { error } = await supabase.from('partner_pickup_packages').insert(row);
  if (error) {
    console.error('[partnerPickupService] insert:', error.message);
    return { error: error.message };
  }
  return { error: null };
}

/**
 * @param {string} packageId
 * @param {string} code
 * @returns {Promise<{ data: object|null, error: string|null }>}
 */
export async function rpcPartnerPickupConfirmReceipt(packageId, code) {
  if (!packageId) return { data: null, error: 'missing_package' };
  const { data, error } = await supabase.rpc('partner_pickup_confirm_receipt', {
    p_package_id: packageId,
    p_code: code ?? '',
  });
  if (error) {
    console.error('[partnerPickupService] confirm_receipt rpc:', error.message);
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

/**
 * @param {string} packageId
 * @param {string} code
 * @returns {Promise<{ data: object|null, error: string|null }>}
 */
export async function rpcPartnerPickupConfirmDelivery(packageId, code) {
  if (!packageId) return { data: null, error: 'missing_package' };
  const { data, error } = await supabase.rpc('partner_pickup_confirm_delivery', {
    p_package_id: packageId,
    p_code: code ?? '',
  });
  if (error) {
    console.error('[partnerPickupService] confirm_delivery rpc:', error.message);
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

/**
 * @param {string} packageId
 * @param {string} notes
 */
export async function rpcPartnerPickupSetNotes(packageId, notes) {
  if (!packageId) return { data: null, error: 'missing_package' };
  const { data, error } = await supabase.rpc('partner_pickup_set_notes', {
    p_package_id: packageId,
    p_notes: notes ?? '',
  });
  if (error) {
    console.error('[partnerPickupService] set_notes rpc:', error.message);
    return { data: null, error: error.message };
  }
  return { data, error: null };
}

/**
 * @param {string} packageId
 * @returns {Promise<{ data: object[], error: string|null }>}
 */
export async function fetchPickupPackageEvents(packageId) {
  if (!packageId) return { data: [], error: 'missing_package' };
  const { data, error } = await supabase
    .from('partner_pickup_package_events')
    .select('id, event_type, detail, created_at')
    .eq('package_id', packageId)
    .order('created_at', { ascending: true })
    .limit(500);
  if (error) {
    console.error('[partnerPickupService] events:', error.message);
    return { data: [], error: error.message };
  }
  return { data: data ?? [], error: null };
}

/** Mensaje legible para `error` devuelto en jsonb por las RPC pickup. */
export function partnerPickupRpcUserMessage(errKey) {
  const k = String(errKey || '').trim();
  if (k === 'not_found') return 'No se encontró el paquete o no pertenece a tu partner.';
  if (k === 'bad_status') return 'El paquete no está en el estado esperado para esta acción.';
  if (k === 'bad_code') return 'El código no coincide.';
  return k || 'No se pudo completar la operación.';
}
